/**
 * Adaptive Tick Generator (T-06) - ENHANCED WITH T-10 SUPPORT
 * 
 * Intelligently generates tick marks for number lines based on:
 * - Current zoom level (pixels per unit)
 * - Base unit interval (fractions, decimals, integers)
 * - Target spacing density (50-120px for major ticks)
 * - Number type and display preferences
 * - T-10: Custom ticks and enhanced subtick generation
 * - T-25: Enhanced tick label formatting with locale support
 * 
 * Key Features:
 * - Fraction-friendly denominator selection
 * - Adaptive major/minor tick classification
 * - Sub-tick generation for enhanced precision
 * - Support for integer, decimal, fraction, and percentage modes
 * - T-10: Custom tick integration and midpoint support
 * - T-25: Locale-aware number formatting with decimal separator support
 */

import { Fraction } from '../types/redux';
import { formatFraction, toNumber } from './fractions';
import { Domain } from './scale';
import { formatNumberWithPrecision, LocaleCode } from './locale';

// T-10: Import custom tick types
interface CustomTick {
  id: string;
  value: number;
  label?: string;
  type: 'midpoint' | 'custom';
  createdAt: number;
}

/**
 * Represents a single tick mark on the number line
 * ENHANCED FOR T-10: Added source attribution and custom tick reference
 * ENHANCED FOR Hierarchical Ticks: Added level and showLabel for progressive zoom
 */
export interface Tick {
  /** The numeric value of this tick */
  value: number;
  /** Whether this is a major tick (gets a label) or minor tick */
  major: boolean;
  /** Optional label text for this tick */
  label?: string;
  /** Hierarchical level: 0 = primary, 1 = half, 2 = quarter, 3 = tenth/eighth, etc. */
  level: number;
  /** Whether the label should be shown at current zoom level */
  showLabel: boolean;
  /** T-10: Source of this tick */
  source?: 'generated' | 'custom' | 'midpoint';
  /** T-10: Reference to custom tick if applicable */
  customTick?: CustomTick;
}

/**
 * Subdivision mode for hierarchical tick generation
 */
export type SubdivisionMode = 'smart' | 'decimal' | 'binary' | 'halves-tenths';

/**
 * Subdivision pattern definitions for each mode
 */
export const SUBDIVISION_PATTERNS: Record<SubdivisionMode, number[]> = {
  // Smart: uses context-aware subdivision based on unit interval
  smart: [2, 4, 10, 100],
  // Decimal: powers of 10 (10ths, 100ths, 1000ths)
  decimal: [10, 100, 1000],
  // Binary: halves, quarters, eighths, sixteenths
  binary: [2, 4, 8, 16],
  // Halves-tenths: halves, quarters, then tenths
  'halves-tenths': [2, 4, 10],
};

/**
 * Configuration options for tick generation
 * ENHANCED FOR T-10: Added custom ticks and subtick options
 * ENHANCED FOR Hierarchical Ticks: Added subdivision mode
 */
export interface TickGenerationOptions {
  /** Minimum pixels between major ticks (default: 50) */
  minPxMajor?: number;
  /** Maximum pixels between major ticks (default: 120) */
  maxPxMajor?: number;
  /** Number of subdivisions for minor ticks (default: auto-calculated) */
  subDiv?: number;
  /** Fraction display preferences */
  fractionDisplay: {
    format: 'mixed' | 'improper';
    simplify: boolean;
  };
  /** Whether to force integer labels even for fractional units */
  forceIntegerLabels?: boolean;
  /** Number type determines label formatting */
  numberType?: 'integer' | 'decimal' | 'fraction' | 'percent';
  /** T-10: Custom ticks to incorporate */
  customTicks?: CustomTick[];
  /** T-10: Whether to show enhanced subticks */
  showSubticks?: boolean;
  /** T-10: Minimum pixels between subticks */
  minPxSubtick?: number;
  /** T-10: Maximum subtick subdivisions */
  maxSubdivisions?: number;
  /** Hierarchical: Subdivision mode for progressive zoom levels */
  subdivisionMode?: SubdivisionMode;
  /** Hierarchical: Minimum pixel spacing before showing labels at a level */
  minPxForLabel?: number;
}

/**
 * Default tick generation options
 * ENHANCED FOR T-10: Added new defaults
 * ENHANCED FOR Hierarchical Ticks: Added subdivision mode defaults
 */
export const DEFAULT_TICK_OPTIONS: Required<Omit<TickGenerationOptions, 'customTicks'>> = {
  minPxMajor: 50,
  maxPxMajor: 120,
  subDiv: 0, // Auto-calculated
  fractionDisplay: {
    format: 'mixed',
    simplify: true,
  },
  forceIntegerLabels: false,
  numberType: 'fraction',
  showSubticks: true,
  minPxSubtick: 8,
  maxSubdivisions: 4,
  subdivisionMode: 'smart',
  minPxForLabel: 40, // Minimum pixel spacing to show labels at a tick level
};

/**
 * Optional per-level label spacing overrides for specific modes.
 * Falls back to the global minPxForLabel when not specified.
 */
function getLevelMinLabelSpacing(mode: SubdivisionMode, defaultSpacing: number): number[] {
  switch (mode) {
    case 'decimal':
      // Show tenths labels earlier so they appear around ~162px primary spacing
      return [
        defaultSpacing,          // primary
        Math.min(16, defaultSpacing), // tenths (level 1 in decimal mode)
        defaultSpacing,          // hundredths+
      ];
    case 'halves-tenths':
      // Allow tenths to show labels earlier than the global default.
      // Levels: 0=primary,1=halves,2=quarters,3=tenths,4=hundredths
      return [
        defaultSpacing, // primary
        defaultSpacing, // halves
        Math.min(20, defaultSpacing), // quarters (keep visible until tenths appear)
        Math.min(12, defaultSpacing), // tenths (show as soon as visible)
        Math.min(18, defaultSpacing), // hundredths
      ];
    default:
      return [];
  }
}

/**
 * Preferred denominators for fraction-friendly tick spacing
 * These create intuitive subdivisions that align with mathematical learning
 */
const PREFERRED_DENOMINATORS = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 16, 20, 24, 25, 30, 50, 100];

/**
 * Common decimal multiples for clean decimal tick spacing
 */
const DECIMAL_MULTIPLES = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100];

/**
 * T-25 INTEGRATION POINT: Enhanced generateTicks function signature
 * This shows how the generateTicks function needs to accept locale
 * 
 * Generate adaptive tick marks for a number line
 * ENHANCED FOR T-10: Now supports custom ticks and enhanced subtick generation
 * ENHANCED FOR T-25: Now supports locale-aware number formatting
 * 
 * @param domain - The visible range of values
 * @param unit - The base unit interval as a fraction
 * @param pxPerUnit - Current scale (pixels per unit)
 * @param width - Screen width in pixels
 * @param options - Tick generation options (now includes T-10 features)
 * @param locale - Locale for number formatting (T-25)
 * @returns Array of tick objects with positions and labels
 * 
 * @example
 * // Generate ticks for unit interval 1/4, moderately zoomed, with custom ticks
 * const ticks = generateTicks(
 *   { start: 0, end: 4 },
 *   { n: 1, d: 4 },
 *   80, // 80 pixels per unit
 *   800,
 *   { 
 *     fractionDisplay: { format: 'mixed', simplify: true },
 *     customTicks: [{ id: '1', value: 1.5, type: 'midpoint', createdAt: Date.now() }]
 *   },
 *   'fr-FR' // T-25: Locale for number formatting
 * );
 */
export function generateTicks(
  domain: Domain,
  unit: Fraction,
  pxPerUnit: number,
  width: number,
  options: Partial<TickGenerationOptions> = {},
  locale: LocaleCode = 'en-GB' // T-25: Added locale parameter
): Tick[] {
  const opts = { ...DEFAULT_TICK_OPTIONS, ...options };
  
  // Calculate base unit in decimal for easier math
  const unitDecimal = toNumber(unit);
  const currentSpacing = pxPerUnit * unitDecimal;
  
  // Determine the appropriate tick interval and subdivision
  const { majorInterval, subdivisions } = calculateTickInterval(
    unitDecimal,
    currentSpacing,
    opts,
    unit
  );
  
  // Generate the base tick positions with hierarchical level info
  let ticks = generateTickPositions(domain, majorInterval, subdivisions, opts, pxPerUnit);
  
  // T-10: Incorporate custom ticks
  if (options.customTicks && options.customTicks.length > 0) {
    ticks = incorporateCustomTicks(ticks, options.customTicks, opts);
  }
  
  // T-10: Generate enhanced subticks if enabled
  if (opts.showSubticks && opts.minPxSubtick) {
    ticks = addEnhancedSubticks(ticks, unit, pxPerUnit, domain, opts);
  }
  
  // Generate hierarchical tick levels based on subdivision mode
  ticks = applyHierarchicalLevels(ticks, pxPerUnit, opts);
  
  // T-25: When adding labels to ticks that should show them, pass locale
  return ticks.map(tick => ({
    ...tick,
    label: tick.showLabel ? 
      formatTickLabel(tick.value, unit, opts, tick.source, locale) :
      undefined
  }));
}

/**
 * Apply hierarchical levels to ticks based on subdivision mode and zoom level
 * This recalculates levels for all ticks to ensure consistency
 * Ticks are assigned levels based on their position relative to primary intervals
 */
function applyHierarchicalLevels(
  ticks: Tick[],
  pxPerUnit: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>
): Tick[] {
  // Sort ticks by value
  const sortedTicks = [...ticks].sort((a, b) => a.value - b.value);
  
  // Find the primary (major) tick interval from level 0 ticks
  const level0Ticks = sortedTicks.filter(t => t.major && t.level === 0);
  let primaryInterval = 1;
  if (level0Ticks.length >= 2) {
    primaryInterval = Math.abs(level0Ticks[1].value - level0Ticks[0].value);
  }
  
  const primarySpacingPx = primaryInterval * pxPerUnit;
  
  // Get the subdivision sequence for this mode
  const subdivisionSequence = getSubdivisionSequence(opts.subdivisionMode);
  
  // Calculate what spacing each level would have
  const levelSpacings: number[] = [primarySpacingPx]; // Level 0
  let currentSpacing = primarySpacingPx;
  subdivisionSequence.forEach((subdivFactor, idx) => {
    // In halves-tenths mode, reset to primary spacing when entering tenths (index 2)
    const baseSpacing =
      opts.subdivisionMode === 'halves-tenths' && idx === 2
        ? primarySpacingPx
        : currentSpacing;
    const nextSpacing = baseSpacing / subdivFactor;
    levelSpacings.push(nextSpacing);
    currentSpacing = nextSpacing;
  });
  
  // Determine which levels are visible based on their spacing requirements
  const levelMinSpacing = getLevelMinSpacing(opts.subdivisionMode);
  const levelVisible: boolean[] = levelSpacings.map((spacing, level) => {
    const minRequired = levelMinSpacing[level] || 10;
    return spacing >= minRequired;
  });
  
  // Determine which levels should show labels
  const labelMinSpacing = getLevelMinLabelSpacing(opts.subdivisionMode, opts.minPxForLabel);
  const levelShowLabels: boolean[] = levelSpacings.map((spacing, level) => {
    const minRequired = labelMinSpacing[level] ?? opts.minPxForLabel;
    return spacing >= minRequired;
  });

  // Halves → Tenths mode: when tenths are visible, show halves (level 1) and tenths (level 3); hide others
  if (opts.subdivisionMode === 'halves-tenths' && levelVisible[3]) {
    for (let level = 1; level < levelShowLabels.length; level++) {
      if (level === 1 || level === 3) continue;
      levelShowLabels[level] = false;
    }
  }
  
  return sortedTicks.map(tick => {
    // Custom and midpoint ticks always show labels
    if (tick.source === 'custom' || tick.source === 'midpoint') {
      return { ...tick, level: 0, showLabel: true };
    }
    
    const level = tick.level;
    const isVisible = levelVisible[level] ?? false;
    const showLabel = isVisible && (levelShowLabels[level] ?? false);
    
    return { ...tick, showLabel };
  });
}

/**
 * T-10: Utility function to calculate precise midpoints without floating point errors
 */
function calculatePreciseMidpoint(start: number, end: number): number {
  const rawMidpoint = (start + end) / 2;
  // Round to 10 decimal places to eliminate floating point precision errors
  return Math.round(rawMidpoint * 1e10) / 1e10;
}

/**
 * T-10: Incorporate custom ticks into the tick list
 */
function incorporateCustomTicks(
  baseTicks: Tick[],
  customTicks: CustomTick[],
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>
): Tick[] {
  const result = [...baseTicks];
  const tolerance = 1e-10;

  for (const customTick of customTicks) {
    // Check if a tick already exists at this position
    const existingTick = baseTicks.find(tick => 
      Math.abs(tick.value - customTick.value) < tolerance
    );

    if (!existingTick) {
      // Add as a new major tick - custom ticks are always level 0 and show labels
      result.push({
        value: customTick.value,
        major: true,
        level: 0,
        showLabel: true,
        source: customTick.type === 'midpoint' ? 'midpoint' : 'custom',
        customTick,
      });
    } else if (existingTick && !existingTick.major) {
      // Upgrade existing minor tick to major if it matches custom tick
      existingTick.major = true;
      existingTick.level = 0;
      existingTick.showLabel = true;
      existingTick.source = customTick.type === 'midpoint' ? 'midpoint' : 'custom';
      existingTick.customTick = customTick;
    }
  }

  return result.sort((a, b) => a.value - b.value);
}

/**
 * Get the subdivision sequence for a given mode
 * Returns array of subdivision factors for each level
 */
function getSubdivisionSequence(mode: SubdivisionMode): number[] {
  switch (mode) {
    case 'decimal':
      // 10ths, then 10ths of those (100ths), then 10ths of those (1000ths)
      return [10, 10, 10, 10];
    case 'binary':
      // Halves, quarters, eighths, sixteenths
      return [2, 2, 2, 2];
    case 'halves-tenths':
      // Halves, quarters, then switch to tenths
      return [2, 2, 10, 10];
    case 'smart':
    default:
      // Smart mode: halves, quarters, eighths, sixteenths (same as binary)
      // This provides clean 1/2, 1/4, 1/8 subdivisions
      return [2, 2, 2, 2];
  }
}

/**
 * Minimum pixel spacing required for each level of subdivision to appear
 * These values are the minimum spacing BETWEEN ticks at that level
 * Thresholds decrease for finer levels to allow progressive appearance
 */
function getLevelMinSpacing(mode: SubdivisionMode): number[] {
  switch (mode) {
    case 'decimal':
      // Decimal mode: tenths appear when they have 12px spacing
      // This means primary needs to be ~120px for tenths to show
      return [50, 12, 12, 12, 8]; // levels 0, 1, 2, 3, 4
    case 'binary':
    case 'smart':
    default:
      // Binary/Smart mode: progressive thresholds for halves, quarters, eighths, sixteenths
      // - Halves appear when halves are 50px apart (primary 100px)
      // - Quarters appear when quarters are 25px apart (primary 100px, halves 50px)
      // - Eighths appear when eighths are 15px apart (primary 120px)
      // - Sixteenths appear when sixteenths are 10px apart (primary 160px)
      return [50, 50, 25, 15, 10];
    case 'halves-tenths':
      // Halves and quarters like binary, then tenths like decimal.
      // Keep hundredths hidden until very zoomed to avoid clutter.
      // Tenths become visible when primary spacing is roughly >= 400px (40px for tenths).
      return [50, 50, 25, 40, 48];
  }
}

/**
 * T-10: Add enhanced subticks based on spacing heuristics
 * ENHANCED FOR Hierarchical Ticks: Generates multiple levels of subdivisions
 * Only generates ticks for levels that have sufficient pixel spacing
 */
function addEnhancedSubticks(
  baseTicks: Tick[],
  unit: Fraction,
  pxPerUnit: number,
  domain: Domain,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>
): Tick[] {
  const result = [...baseTicks];
  const majorTicks = baseTicks.filter(t => t.major).sort((a, b) => a.value - b.value);
  
  if (majorTicks.length < 2) {
    return baseTicks;
  }
  
  // Get the subdivision sequence for this mode
  const subdivisionSequence = getSubdivisionSequence(opts.subdivisionMode);
  
  // Calculate the primary interval
  const primaryInterval = Math.abs(majorTicks[1].value - majorTicks[0].value);
  const primarySpacingPx = primaryInterval * pxPerUnit;
  
  // Determine which levels should be visible based on primary spacing
  // Each level requires progressively more zoom to appear
  let currentInterval = primaryInterval;
  const levelsToGenerate: { level: number; subdivisions: number; interval: number; spacingPx: number }[] = [];
  
  // Get mode-specific spacing thresholds
  const levelMinSpacing = getLevelMinSpacing(opts.subdivisionMode);
  
  for (let levelIndex = 0; levelIndex < subdivisionSequence.length; levelIndex++) {
    const subdivisions = subdivisionSequence[levelIndex];
    // Halves → quarters → tenths: reset to primary interval when introducing tenths
    const baseIntervalForLevel =
      opts.subdivisionMode === 'halves-tenths' && levelIndex === 2
        ? primaryInterval
        : currentInterval;
    const subInterval = baseIntervalForLevel / subdivisions;
    const subSpacingPx = subInterval * pxPerUnit;
    const minSpacingForLevel = levelMinSpacing[levelIndex + 1] || 10;
    
    // Only generate this level if the spacing is >= minimum for this level
    // AND >= the absolute minimum tick spacing
    if (subSpacingPx >= minSpacingForLevel && subSpacingPx >= opts.minPxSubtick) {
      levelsToGenerate.push({
        level: levelIndex + 1, // Level 0 is primary, so subticks start at 1
        subdivisions,
        interval: currentInterval,
        spacingPx: subSpacingPx,
      });
      currentInterval = subInterval;
    } else {
      // Can't fit any more levels
      break;
    }
  }
  
  // Now generate ticks for each level that should be visible
  const existingValues = new Set(baseTicks.map(t => Math.round(t.value * 1e10) / 1e10));
  
  for (const levelInfo of levelsToGenerate) {
    const subInterval = levelInfo.interval / levelInfo.subdivisions;
    // Labels show when spacing is comfortable (>= minPxForLabel)
    const showLabel = levelInfo.spacingPx >= opts.minPxForLabel;
    
    // Generate ticks at this level between all consecutive major ticks
    for (let i = 0; i < majorTicks.length - 1; i++) {
      const startValue = majorTicks[i].value;
      const endValue = majorTicks[i + 1].value;
      const intervalCount = Math.round((endValue - startValue) / subInterval);
      
      for (let j = 1; j < intervalCount; j++) {
        const tickValue = Math.round((startValue + j * subInterval) * 1e10) / 1e10;
        
        // Skip if outside domain or already exists
        if (tickValue < domain.start || tickValue > domain.end) continue;
        if (existingValues.has(tickValue)) continue;
        
        // Determine the actual level based on where this tick falls in the hierarchy
        const actualLevel = determineTickLevel(
          tickValue, 
          startValue, 
          endValue - startValue,
          opts.subdivisionMode
        );
        
        result.push({
          value: tickValue,
          major: false,
          level: actualLevel,
          showLabel: showLabel && actualLevel <= 2, // Only show labels for levels 0-2
          source: 'generated',
        });
        
        existingValues.add(tickValue);
      }
    }
  }

  return result.sort((a, b) => a.value - b.value);
}

/**
 * Determine the hierarchical level of a tick based on its position
 * within a primary interval
 */
function determineTickLevel(
  value: number,
  intervalStart: number,
  intervalWidth: number,
  mode: SubdivisionMode
): number {
  const relativePos = (value - intervalStart) / intervalWidth;
  const tolerance = 1e-9;
  
  // DECIMAL MODE: All tenths are level 1, hundredths are level 2, etc.
  if (mode === 'decimal') {
    // Check if position is a tenth (0.1, 0.2, ..., 0.9)
    for (let i = 1; i <= 9; i++) {
      if (Math.abs(relativePos - i / 10) < tolerance) {
        return 1; // All tenths are level 1
      }
    }
    // Check if position is a hundredth
    for (let i = 1; i <= 99; i++) {
      if (i % 10 !== 0 && Math.abs(relativePos - i / 100) < tolerance) {
        return 2; // Hundredths (not already a tenth) are level 2
      }
    }
    return 3; // Finer subdivisions
  }
  
  // BINARY/SMART MODE: halves, quarters, eighths, sixteenths
  // Check for half position (level 1)
  if (Math.abs(relativePos - 0.5) < tolerance) {
    return 1;
  }
  
  // Check for quarter positions (level 2)
  if (Math.abs(relativePos - 0.25) < tolerance || Math.abs(relativePos - 0.75) < tolerance) {
    return 2;
  }
  
  // Check for eighth positions (level 3) - positions 1/8, 3/8, 5/8, 7/8
  for (let i = 1; i <= 7; i += 2) {
    if (Math.abs(relativePos - i / 8) < tolerance) {
      // In halves-tenths mode, treat eighths as a finer level to avoid labeling with tenths
      return mode === 'halves-tenths' ? 4 : 3;
    }
  }
  
  // Check for sixteenth positions (level 4)
  for (let i = 1; i <= 15; i += 2) {
    if (Math.abs(relativePos - i / 16) < tolerance) {
      return 4;
    }
  }
  
  // HALVES-TENTHS MODE: halves, quarters, then tenths
  if (mode === 'halves-tenths') {
    for (let i = 1; i <= 9; i++) {
      if (Math.abs(relativePos - i / 10) < tolerance) {
        return 3; // Tenths come after quarters in this mode
      }
    }
  }
  
  // Default: finest level
  return 5;
}

/**
 * Calculate the hierarchical level for a subtick based on its position
 * Level 0 = primary ticks
 * Level 1 = halves (1/2)
 * Level 2 = quarters (1/4, 3/4)
 * Level 3 = eighths or tenths
 */
function calculateTickLevel(
  position: number, 
  subdivisions: number,
  mode: SubdivisionMode
): number {
  // Position is 1-based index within the subdivision (e.g., 1,2,3 for 4 subdivisions)
  
  // For binary subdivisions, check if it's at a half, quarter, eighth, etc.
  if (mode === 'binary' || mode === 'halves-tenths' || mode === 'smart') {
    // Check if this position represents a half
    if (subdivisions % 2 === 0 && position === subdivisions / 2) {
      return 1; // Half point
    }
    // Check if this position represents a quarter
    if (subdivisions % 4 === 0 && (position === subdivisions / 4 || position === 3 * subdivisions / 4)) {
      return 2; // Quarter point
    }
    // Check if this position represents an eighth
    if (subdivisions % 8 === 0 && 
        (position === subdivisions / 8 || position === 3 * subdivisions / 8 ||
         position === 5 * subdivisions / 8 || position === 7 * subdivisions / 8)) {
      // In halves-tenths mode, treat eighths as finer than tenths to suppress their labels
      return mode === 'halves-tenths' ? 4 : 3; // Eighth point
    }
  }
  
  // For decimal subdivisions
  if (mode === 'decimal') {
    // Fifths (0.2, 0.4, 0.6, 0.8) are level 2
    if (subdivisions === 10 && position % 2 === 0) {
      return 2;
    }
    // All other tenths are level 3
    return 3;
  }
  // Halves-tenths falls through to default sizing
  
  // Default: assign level based on how "fine" the subdivision is
  if (subdivisions <= 2) return 1;
  if (subdivisions <= 4) return 2;
  if (subdivisions <= 10) return 3;
  return 4;
}

/**
 * T-10: Calculate optimal subtick subdivisions
 */
function calculateSubtickSubdivisions(
  unit: Fraction,
  pxPerUnit: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>
): { subdivisions: number; spacingPx: number; shouldShow: boolean } {
  const unitDecimal = toNumber(unit);
  const majorTickSpacing = unitDecimal * pxPerUnit;
  
  // Try different subdivision factors (prefer common ones)
  const candidates = [2, 4, 5, 10];
  
  for (const subdivisions of candidates) {
    if (subdivisions > opts.maxSubdivisions) continue;
    
    const subtickSpacing = majorTickSpacing / subdivisions;
    
    if (subtickSpacing >= opts.minPxSubtick) {
      return {
        subdivisions,
        spacingPx: subtickSpacing,
        shouldShow: true,
      };
    }
  }

  return { subdivisions: 0, spacingPx: 0, shouldShow: false };
}

/**
 * Calculate the optimal tick interval and subdivisions based on current spacing
 */
function calculateTickInterval(
  unitDecimal: number,
  currentSpacing: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  originalUnit: Fraction
): { majorInterval: number; subdivisions: number } {
  
  // If current spacing is within target range, use the base unit
  if (currentSpacing >= opts.minPxMajor && currentSpacing <= opts.maxPxMajor) {
    const subdivisions = opts.subDiv || calculateSubdivisions(originalUnit);
    return {
      majorInterval: unitDecimal,
      subdivisions: Math.max(2, subdivisions), // Ensure at least 2 subdivisions for minor ticks
    };
  }
  
  // If spacing is too small (zoomed out), find a larger interval
  if (currentSpacing < opts.minPxMajor) {
    return findLargerInterval(unitDecimal, currentSpacing, opts, originalUnit);
  }
  
  // If spacing is too large (zoomed in), find subdivisions
  return findSmallerInterval(unitDecimal, currentSpacing, opts, originalUnit);
}

/**
 * Find a larger interval when current spacing is too small (zoomed out)
 */
function findLargerInterval(
  unitDecimal: number,
  currentSpacing: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  originalUnit: Fraction
): { majorInterval: number; subdivisions: number } {
  
  // Try integer multiples first
  for (let multiplier = 2; multiplier <= 100; multiplier++) {
    const interval = unitDecimal * multiplier;
    const spacing = currentSpacing * multiplier;
    
    if (spacing >= opts.minPxMajor && spacing <= opts.maxPxMajor) {
      return {
        majorInterval: interval,
        subdivisions: calculateSubdivisions(originalUnit, multiplier),
      };
    }
  }
  
  // If unit-based multiples don't work, try preferred intervals
  for (const multiple of DECIMAL_MULTIPLES) {
    const interval = multiple;
    const spacing = interval / unitDecimal * currentSpacing;
    
    if (spacing >= opts.minPxMajor && spacing <= opts.maxPxMajor) {
      return {
        majorInterval: interval,
        subdivisions: Math.max(2, Math.round(interval / unitDecimal)),
      };
    }
  }
  
  // Fallback: use a power of 10
  const magnitude = Math.pow(10, Math.floor(Math.log10(unitDecimal)));
  return {
    majorInterval: magnitude,
    subdivisions: Math.max(2, Math.round(magnitude / unitDecimal)),
  };
}

/**
 * Find subdivisions when current spacing is too large (zoomed in)
 */
function findSmallerInterval(
  unitDecimal: number,
  currentSpacing: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  originalUnit: Fraction
): { majorInterval: number; subdivisions: number } {
  
  // Calculate how many subdivisions we need
  const targetSubdivisions = Math.ceil(currentSpacing / opts.maxPxMajor);
  
  // Try to find a fraction-friendly subdivision
  const preferredSubdivs = [2, 3, 4, 5, 6, 8, 10, 12, 15, 16, 20];
  
  for (const subdiv of preferredSubdivs) {
    if (subdiv >= targetSubdivisions) {
      const subSpacing = currentSpacing / subdiv;
      if (subSpacing >= opts.minPxMajor * 0.5) { // Allow slightly closer spacing for subdivisions
        return {
          majorInterval: unitDecimal,
          subdivisions: subdiv,
        };
      }
    }
  }
  
  // Fallback to calculated subdivisions
  return {
    majorInterval: unitDecimal,
    subdivisions: Math.max(2, targetSubdivisions),
  };
}

/**
 * Calculate appropriate subdivisions for a given unit and multiplier
 */
function calculateSubdivisions(unit: Fraction, multiplier: number = 1): number {
  // For fractions, try to create intuitive subdivisions
  if (unit.d > 1) {
    // Common subdivision patterns for fractions
    const patterns = [2, 3, 4, 5, unit.d];
    return patterns.find(p => p <= 10) || 4;
  }
  
  // For integers and simple units, always use some subdivisions for better granularity
  if (multiplier <= 2) return 2;
  if (multiplier <= 5) return multiplier;
  if (multiplier <= 10) return 5;
  return 10;
}

/**
 * Generate the actual tick positions within the domain
 * ONLY generates major (level 0) ticks - subticks are handled by addEnhancedSubticks
 */
function generateTickPositions(
  domain: Domain,
  majorInterval: number,
  subdivisions: number,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  pxPerUnit: number = 100 // Default for backward compatibility
): Tick[] {
  const ticks: Tick[] = [];
  
  // Prevent excessive tick generation
  const maxTicks = 1000;
  const domainWidth = Math.abs(domain.end - domain.start);
  const estimatedTicks = (domainWidth + 2 * majorInterval) / majorInterval;
  
  if (estimatedTicks > maxTicks) {
    // Use a larger interval to prevent excessive ticks
    const adjustedMajorInterval = (domainWidth + 2 * majorInterval) / maxTicks;
    return generateTickPositions(domain, adjustedMajorInterval, subdivisions, opts, pxPerUnit);
  }
  
  // Find the first major tick at or before domain start
  const firstMajorTick = Math.floor(domain.start / majorInterval) * majorInterval;
  
  // Generate ONLY major ticks (level 0) - subticks handled by addEnhancedSubticks
  const startValue = firstMajorTick - majorInterval;
  const endValue = domain.end + majorInterval;
  
  // Calculate pixel spacing for label visibility decisions
  const majorSpacingPx = majorInterval * pxPerUnit;
  
  for (let value = startValue; value <= endValue; value += majorInterval) {
    // Handle floating point precision
    const roundedValue = Math.round(value * 1e10) / 1e10;
    
    // Skip if outside reasonable bounds
    if (roundedValue < domain.start - majorInterval || 
        roundedValue > domain.end + majorInterval) {
      continue;
    }
    
    ticks.push({
      value: roundedValue,
      major: true,
      level: 0,
      showLabel: majorSpacingPx >= opts.minPxForLabel,
      source: 'generated',
    });
  }
  
  return ticks;
}

/**
 * Enhanced formatTickLabel with T-25 locale support
 * T-25 CHANGE: Added locale parameter throughout the formatting pipeline
 * HIERARCHICAL TICKS: Non-integer values always show as decimals, even in integer mode
 */
function formatTickLabel(
  value: number,
  originalUnit: Fraction,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  tickSource: 'generated' | 'custom' | 'midpoint' = 'generated',
  locale: LocaleCode = 'en-GB' // T-25: Added locale parameter with default
): string {
  const isInteger = Math.abs(value - Math.round(value)) < 1e-10;
  
  switch (opts.numberType) {
    case 'integer':
      // In integer mode: show integers as integers, but non-integers as decimals
      // This ensures subticks like 0.5 show correctly instead of being rounded
      if (isInteger) {
        return formatNumberWithPrecision(Math.round(value), locale, 0);
      }
      // Non-integer values: show as decimals to preserve the actual value
      return formatDecimalLabel(value, locale);
      
    case 'decimal':
      return formatDecimalLabel(value, locale); // T-25: Pass locale
      
    case 'percent':
      // Handle zero case for percentages
      if (Math.abs(value) < 1e-10) {
        return '0.0%';
      }
      // T-25: Format percentage value with locale, then add %
      const percentValue = value * 100;
      return formatNumberWithPrecision(percentValue, locale, 1) + '%';
      
    case 'fraction':
    default:
      // Handle special case of zero for fractions
      if (Math.abs(value) < 1e-10) {
        return '0';
      }
      return formatFractionLabel(value, originalUnit, opts, locale); // T-25: Pass locale
  }
}

/**
 * Enhanced formatDecimalLabel with T-25 locale support
 * T-25 CHANGE: Now accepts locale parameter and uses locale-aware formatting
 */
function formatDecimalLabel(value: number, locale: LocaleCode): string {
  // T-25: Use locale-aware formatting with precision from T-10
  return formatNumberWithPrecision(value, locale, 4);
}

/**
 * Enhanced formatFractionLabel with T-25 locale support
 * T-25 CHANGE: Added locale parameter for mixed number formatting
 * 
 * When simplify is false, we compute fractions using the original unit interval's 
 * denominator to preserve the unsimplified form (e.g., 2/10 instead of 1/5).
 */
function formatFractionLabel(
  value: number,
  originalUnit: Fraction,
  opts: Required<Omit<TickGenerationOptions, 'customTicks'>>,
  locale: LocaleCode // T-25: Added locale parameter
): string {
  // For integer values, just return the integer
  if (Math.abs(value - Math.round(value)) < 1e-10 && opts.forceIntegerLabels) {
    return formatNumberWithPrecision(Math.round(value), locale, 0); // T-25: Use locale
  }
  
  try {
    let fraction: Fraction;
    
    if (!opts.fractionDisplay.simplify) {
      // When NOT simplifying, compute fraction using a denominator based on the unit interval
      // This preserves forms like 2/10, 4/10 instead of 1/5, 2/5
      fraction = computeUnsimplifiedFraction(value, originalUnit);
    } else {
      // When simplifying, use the standard approximation which returns simplified fractions
      const maxDenominator = Math.max(originalUnit.d, 100);
      fraction = approximateDecimalToFraction(value, maxDenominator);
    }
    
    // T-25: Pass locale to formatFraction (map unsupported locales to supported ones)
    const supportedLocale = locale === 'en-US' ? 'en-GB' : 
                           locale === 'es-ES' ? 'fr-FR' : 
                           locale;
    return formatFraction(fraction, {
      mixed: opts.fractionDisplay.format === 'mixed',
      simplify: opts.fractionDisplay.simplify,
      locale: supportedLocale as 'en-GB' | 'fr-FR' | 'de-DE' // T-25: Map to supported locales
    });
  } catch {
    // Fallback to decimal if fraction conversion fails
    return formatDecimalLabel(value, locale); // T-25: Pass locale
  }
}

/**
 * Compute a fraction for a value WITHOUT simplifying.
 * 
 * The key insight is that we want to preserve "natural" denominators.
 * For decimal-like values (0.1, 0.2, 0.25, etc.), we prefer powers of 10.
 * For fraction-like values, we prefer common denominators (2, 3, 4, 5, 6, 8, 10, 12, etc.)
 * 
 * For example:
 * - value 0.2 -> 2/10 (not 1/5)
 * - value 0.5 -> 5/10 (not 1/2) when coming from tenths
 * - value 0.25 -> 25/100 or 1/4 depending on context
 */
function computeUnsimplifiedFraction(value: number, originalUnit: Fraction): Fraction {
  if (value === 0) return { n: 0, d: 1 };
  
  const sign = value < 0 ? -1 : 1;
  const absValue = Math.abs(value);
  
  // Check if value is an integer
  if (Math.abs(absValue - Math.round(absValue)) < 1e-10) {
    return { n: sign * Math.round(absValue), d: 1 };
  }
  
  // Strategy: Try denominators in order of preference for unsimplified display
  // Prefer larger "round" denominators that give clean representations
  // This ensures 0.2 -> 2/10, 0.5 -> 5/10, etc.
  
  // First, try powers of 10 (these are "decimal-friendly" denominators)
  const powersOf10 = [10, 100, 1000];
  for (const d of powersOf10) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }
  
  // Try the original unit's denominator and its multiples
  const baseDenominator = originalUnit.d;
  if (baseDenominator > 1) {
    const unitMultipliers = [1, 2, 4, 5, 10];
    for (const mult of unitMultipliers) {
      const d = baseDenominator * mult;
      const n = absValue * d;
      if (Math.abs(n - Math.round(n)) < 1e-10) {
        return { n: sign * Math.round(n), d };
      }
    }
  }
  
  // Try common fraction denominators (in order of preference for educational use)
  const commonDenominators = [2, 3, 4, 5, 6, 8, 12, 15, 16, 20, 24, 25, 30, 50];
  for (const d of commonDenominators) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }
  
  // Fallback: find any denominator up to 1000 that works
  for (let d = 1; d <= 1000; d++) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }
  
  // Last resort: approximate with denominator 100
  return { n: sign * Math.round(absValue * 100), d: 100 };
}

/**
 * Convert a decimal value to the best approximating fraction
 * with a reasonable denominator limit
 */
function approximateDecimalToFraction(decimal: number, maxDenominator: number = 100): Fraction {
  if (decimal === 0) return { n: 0, d: 1 };
  
  const sign = decimal < 0 ? -1 : 1;
  const absDecimal = Math.abs(decimal);
  
  // Handle simple cases first
  const commonFractions: [number, Fraction][] = [
    [0.5, { n: 1, d: 2 }],
    [0.25, { n: 1, d: 4 }],
    [0.75, { n: 3, d: 4 }],
    [0.333333, { n: 1, d: 3 }],
    [0.666667, { n: 2, d: 3 }],
    [0.2, { n: 1, d: 5 }],
    [0.4, { n: 2, d: 5 }],
    [0.6, { n: 3, d: 5 }],
    [0.8, { n: 4, d: 5 }],
    [0.125, { n: 1, d: 8 }],
    [0.375, { n: 3, d: 8 }],
    [0.625, { n: 5, d: 8 }],
    [0.875, { n: 7, d: 8 }],
  ];
  
  for (const [value, fraction] of commonFractions) {
    if (Math.abs(absDecimal - value) < 1e-6) {
      return { n: sign * fraction.n, d: fraction.d };
    }
  }
  
  let bestN = 0;
  let bestD = 1;
  let bestError = Math.abs(absDecimal);
  
  for (let d = 1; d <= maxDenominator; d++) {
    const n = Math.round(absDecimal * d);
    const error = Math.abs(absDecimal - n / d);
    
    if (error < bestError) {
      bestError = error;
      bestN = n;
      bestD = d;
      
      // If we found an exact match, stop searching
      if (error < 1e-10) break;
    }
  }
  
  return { n: sign * bestN, d: bestD };
}

/**
 * Get tick spacing information for debugging and testing
 * ENHANCED FOR T-10: Now includes subtick information
 */
export function getTickSpacingInfo(
  unit: Fraction,
  pxPerUnit: number,
  options: Partial<TickGenerationOptions> = {}
): {
  baseSpacing: number;
  majorInterval: number;
  subdivisions: number;
  effectiveSpacing: number;
  subtickInfo?: { subdivisions: number; spacingPx: number; shouldShow: boolean };
} {
  const opts = { ...DEFAULT_TICK_OPTIONS, ...options };
  const unitDecimal = toNumber(unit);
  const baseSpacing = pxPerUnit * unitDecimal;
  
  const { majorInterval, subdivisions } = calculateTickInterval(
    unitDecimal,
    baseSpacing,
    opts,
    unit
  );
  
  // T-10: Add subtick information
  const subtickInfo = opts.showSubticks ? 
    calculateSubtickSubdivisions(unit, pxPerUnit, opts) : undefined;
  
  return {
    baseSpacing,
    majorInterval,
    subdivisions,
    effectiveSpacing: (majorInterval / unitDecimal) * baseSpacing / subdivisions,
    subtickInfo,
  };
}

/**
 * Generate ticks for a specific range with custom step size
 * Useful for creating custom tick patterns
 */
export function generateCustomTicks(
  start: number,
  end: number,
  step: number,
  majorEvery: number = 1,
  labelFormatter?: (value: number) => string
): Tick[] {
  const ticks: Tick[] = [];
  let tickIndex = 0;
  
  for (let value = start; value <= end + step * 0.01; value += step) {
    const roundedValue = Math.round(value / step) * step;
    const isMajor = tickIndex % majorEvery === 0;
    
    // Calculate level based on how many intervals since last major
    const posInMajorInterval = tickIndex % majorEvery;
    let level = 0;
    if (!isMajor) {
      level = calculateTickLevel(posInMajorInterval, majorEvery, 'smart');
    }
    
    ticks.push({
      value: roundedValue,
      major: isMajor,
      level,
      showLabel: isMajor, // Only show labels on major ticks for custom generation
      label: isMajor && labelFormatter ? labelFormatter(roundedValue) : undefined,
      source: 'generated',
    });
    
    tickIndex++;
  }
  
  return ticks;
}

/**
 * Filter ticks to only those visible within a domain
 */
export function filterVisibleTicks(ticks: Tick[], domain: Domain): Tick[] {
  return ticks.filter(tick => 
    tick.value >= domain.start && tick.value <= domain.end
  );
}

// T-10: Selectors for custom ticks (moved from customTicksSlice for use here)
export const selectCustomTicksInDomain = (
  customTicks: { ticks: CustomTick[] },
  domain: { start: number; end: number },
  equivalentDenominator?: number
) => {
  return customTicks.ticks.filter(
    (tick) =>
      tick.value >= domain.start &&
      tick.value <= domain.end &&
      (equivalentDenominator === undefined ||
        (tick.equivalentDenominator ?? null) === (equivalentDenominator ?? null))
  );
};

export const selectMidpointTicks = (customTicks: { ticks: CustomTick[] }) => {
  return customTicks.ticks.filter(tick => tick.type === 'midpoint');
};

export const selectCustomTicksSorted = (customTicks: { ticks: CustomTick[] }) => {
  return [...customTicks.ticks].sort((a, b) => a.value - b.value);
};